import { lighten } from "@mui/system";
import styled from "styled-components";

export const ArticleBodyStyles = styled.div(({ theme }) => ({
  "& a": {
    color: "inherit",

    "&:hover": {
      color: theme.palette.primary.main,
      textDecoration: "none",
    },
  },

  // basic styling for hr
  "& hr": {
    border: `1px solid ${theme.palette.borderColor.primary}`,
    borderWidth: "1px 0 0 0",
    margin: "32px 0",
  },

  // ensure images don't overflow
  "& img": {
    maxWidth: "100%",
  },

  // basic heading styling
  "& h1, & h2, & h3, & h4": {
    fontWeight: 600,
    color: theme.palette.neutral.dark,
    // overwrite for any heading with <b> tags
    "& b": {
      fontWeight: 600,
    },
  },

  "& h1": {
    fontSize: 24,
    lineHeight: "28px",
    marginTop: 40,
    marginBottom: 0,
  },
  "& h2": {
    fontSize: 18,
    lineHeight: "24px",
    marginTop: 48,
    marginBottom: 0,
  },
  "& h3": {
    fontSize: 16,
    lineHeight: "24px",
    marginTop: 40,
    marginBottom: 0,
  },
  "& h4": {
    fontSize: 14,
    lineHeight: "24px",
    marginTop: 32,
    marginBottom: 0,
  },
  // headings followed by other headings should be closer to one another
  // as they are logically related
  "& h2 + h3": { marginTop: 24 },
  "& h3 + h4": { marginTop: 16 },

  "& p": {
    fontSize: 14,
    lineHeight: "24px",
    marginTop: 16,
    marginBottom: 0,
  },

  "& .intercom-interblocks-callout": {
    marginTop: 32,
    padding: "24px 16px",
    [`${theme.breakpoints.up("sm")}`]: {
      padding: "32px",
    },

    backgroundColor: `${theme.palette.backgroundColor.info.light} !important`,
    borderRadius: `${theme.shape.borderRadii.large}px`,

    "& > *:first-child": {
      marginTop: 0,
    },
  },

  // Intercom Video Sizing
  "& .intercom-h2b-video": {
    "& iframe": {
      width: "100%",
      height: "100%",
      aspectRatio: "16/9",
    },
  },

  // --------------------------------
  // ACCORDION
  // --------------------------------

  // The first accordion in any set of accordions gets some
  // top margin and a top border (as well as a border like all
  // other accordions do)
  "& details:not(details + details)": {
    marginTop: "24px",
  },

  "& details": {
    border: `1px solid ${theme.palette.borderColor.primary}`,
    borderRadius: "0.75rem",
    overflow: "hidden",
    marginBottom: "0.6em",
    "& > :not(summary)": {
      transition: "all 0.3s ease",
    },
    "& summary": {
      alignItems: "center",
      backgroundColor: "initial",
      borderRadius: "0.75rem",
      color: "rgb(80 80 80 / 1)", // Replace var(--tw-text-opacity) with 1 or your preferred value
      display: "flex",
      fontSize: "0.875rem",
      fontWeight: 500,
      lineHeight: "1.5rem",
      padding: "1rem 3rem 1rem 1rem",
      position: "relative",
      "&:hover": {
        backgroundColor: lighten(theme.palette.backgroundColor.grey, 0.1),
        cursor: "pointer",
      },
      "& > h2, & > h3": { fontSize: "0.875rem" },
      "& > h2, & > h3, & > h4": {
        position: "relative",
        fontWeight: 500,
        margin: "0 !important",
        color: theme.palette.neutral.main,
        "& > b": {
          fontWeight: 500,
        },
      },

      // open icon
      "&::after": {
        backgroundImage:
          "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' aria-hidden='true' viewBox='0 0 24 24' data-id='IconChevronDown'%3E%3Cpath fill='currentColor' fill-rule='evenodd' d='M.502 6.502c-.67.67-.67 1.755 0 2.424l9.68 9.68a2.572 2.572 0 0 0 3.636 0l9.68-9.68a1.714 1.714 0 0 0-2.424-2.424L12 15.576 2.926 6.502a1.714 1.714 0 0 0-2.424 0Z' clip-rule='evenodd'/%3E%3C/svg%3E\")",
        backgroundRepeat: "no-repeat",
        backgroundSize: "12px",
        content: "''",
        display: "inline-block",
        height: "12px",
        position: "absolute",
        right: "1rem",
        transition: "transform 0.3s ease",
        width: "12px",
      },

      // hide default arrow
      "&::marker": {
        content: "none",
      },
      "&::-webkit-details-marker": {
        display: "none",
      },

      "&:hover > h2::after, &:hover > h3::after, &:hover > h4::after": {
        backgroundColor: theme.palette.background.default,
      },
    },
    // close (-) icon
    "&[open] > summary::after": {
      transform: "rotate(180deg)",
    },
    "& > div": {
      paddingBottom: 16,
      "& ul": {
        marginBottom: 0,
      },
    },
    "& .collapsible-section-content": {
      padding: "10px 15px",
    },
  },

  // existing CSS
  "& table": {
    borderCollapse: "collapse",
    border: `1px solid ${theme.palette.borderColor.primary}`,
  },

  "& td": {
    verticalAlign: "top",
    textAlign: "left",
    padding: 5,
    p: {
      marginBlockStart: 0,
      marginBlockEnd: 0,
    },
  },
  "& blockquote": {
    padding: 10,
    backgroundColor: theme.palette.background.default,
    borderLeft: 10,
    borderLeftStyle: "solid",
    borderLeftColor: theme.palette.borderColor.primary,
    marginBlockStart: 0,
    marginBlockEnd: 0,
    marginInlineStart: 0,
    marginInlineEnd: 0,

    "& h3": {
      marginTop: 0,
      marginBottom: 4,
    },

    "& p": {
      marginTop: 4,
      marginBottom: 4,
    },
  },
}));
